import React, { useEffect, useState } from 'react';
import {
  Box,
  Text,
  Spinner,
  Grid,
  GridItem,
  Badge,
  Flex,
  Divider,
} from '@chakra-ui/react';
import { MdWork, MdBusiness } from 'react-icons/md';
import httpInjectorService from 'services/http-injector.service';
import { demoAssignedManagers } from 'components/demodata/Dummydata';

const getInitials = (name = '') =>
  name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2);

const getAvatarColor = (name = '') => {
  const colors = ['#6B46C1', '#3182CE', '#E53E3E', '#38A169', '#D69E2E', '#00B5D8'];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
};

const MyProfileReportingPanel = () => {
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchAssignedManagers = async () => {
    try {
      setLoading(true);
            const isDemoMode = localStorage.getItem("demoMode") === "true";
      
      if (isDemoMode) {
        setManagers(demoAssignedManagers);
        return;
      }
      const res = await httpInjectorService.getUserAssignedManagers();
      if (res?.status === 'success' && res?.data?.length) {
        const data = res.data[0];
        const formattedManagers = [
          data.reporting_manager1_username && {
            id: data.reporting_manager1_user_id,
            name: data.reporting_manager1_username,
            department: data.reporting_manager1_department,
            designation: data.reporting_manager1_designation,
            status: data.reporting_manager1_status,
          },
          data.reporting_manager2_username && {
            id: data.reporting_manager2_user_id,
            name: data.reporting_manager2_username,
            department: data.reporting_manager2_department,
            designation: data.reporting_manager2_designation,
            status: data.reporting_manager2_status,
          },
        ].filter(Boolean);
        setManagers(formattedManagers);
      }
    } catch (error) {
      console.error('Fetch Assigned Managers Error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAssignedManagers();
  }, []);

  return (
    <Box p={2}>

      {/* Header */}
      <Flex align="center" mb={6} gap={2}>
        <Box w="4px" h="22px" bg="purple.500" borderRadius="full" />
        <Text fontWeight="bold" fontSize="lg" mb={0} color="gray.700">
          Reporting Managers
        </Text>
        {managers.length > 0 && (
          <Badge colorScheme="purple" borderRadius="full" px={2}>
            {managers.length}
          </Badge>
        )}
      </Flex>

      {/* Spinner */}
      {loading && (
        <Flex justify="center" align="center" h="120px">
          <Spinner size="lg" color="purple.500" thickness="3px" />
        </Flex>
      )}

      {/* Empty State */}
      {!loading && managers.length === 0 && (
        <Flex
          direction="column"
          align="center"
          justify="center"
          h="120px"
          borderRadius="xl"
          border="1px dashed"
          borderColor="gray.200"
          color="gray.400"
          gap={2}
        >
          <MdBusiness size={28} />
          <Text fontSize="sm">No reporting manager assigned</Text>
        </Flex>
      )}

      {/* Manager Cards Grid */}
      {!loading && managers.length > 0 && (
        <Grid
          templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }}
          gap={4}
        >
          {managers.map((manager, index) => {
            const bg = getAvatarColor(manager.name);
            return (
              <GridItem key={manager.id}>
                <Box
                  bg="white"
                  borderRadius="2xl"
                  border="1px solid"
                  borderColor="gray.100"
                  boxShadow="0 4px 20px rgba(107, 70, 193, 0.08)"
                  overflow="hidden"
                  transition="all 0.25s ease"
                  _hover={{
                    transform: 'translateY(-3px)',
                    boxShadow: '0 12px 32px rgba(107, 70, 193, 0.18)',
                    borderColor: 'purple.100',
                  }}
                >
                  {/* Top color bar */}
                  <Box h="5px" bg={bg} />

                  <Box p={4}>
                    {/* Avatar + Name */}
                    <Flex align="center" gap={3} mb={4}>
                      <Box
                        w="46px"
                        h="46px"
                        borderRadius="full"
                        bg={bg}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        color="white"
                        fontWeight="bold"
                        fontSize="md"
                        flexShrink={0}
                        boxShadow={`0 4px 12px ${bg}55`}
                      >
                        {getInitials(manager.name)}
                      </Box>
                      <Box>
                        <Text fontWeight="bold" fontSize="sm" color="gray.800" mb={0}>
                          {manager.name}
                        </Text>
                        <Badge
                          colorScheme="purple"
                          variant="subtle"
                          fontSize="10px"
                          borderRadius="full"
                        >
                          Manager {index + 1}
                        </Badge>
                      </Box>
                    </Flex>

                    <Divider mb={3} borderColor="gray.100" />

                    {/* Info Rows */}
                    <Flex direction="column" gap={2}>
                      <Flex align="center" gap={2}>
                        <Box color="purple.400" flexShrink={0}>
                          <MdBusiness size={15} />
                        </Box>
                        <Text fontSize="xs" color="gray.500" mb={0} fontWeight="medium">
                          Department
                        </Text>
                        <Text fontSize="xs" color="gray.700" mb={0} ml="auto" fontWeight="semibold">
                          {manager.department || '—'}
                        </Text>
                      </Flex>

                      <Flex align="center" gap={2}>
                        <Box color="purple.400" flexShrink={0}>
                          <MdWork size={15} />
                        </Box>
                        <Text fontSize="xs" color="gray.500" mb={0} fontWeight="medium">
                          Designation
                        </Text>
                        <Text fontSize="xs" color="gray.700" mb={0} ml="auto" fontWeight="semibold">
                          {manager.designation || '—'}
                        </Text>
                      </Flex>
                    </Flex>
                  </Box>
                </Box>
              </GridItem>
            );
          })}
        </Grid>
      )}
    </Box>
  );
};

export default MyProfileReportingPanel;